import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import { create as createFedopsLogger } from '@wix/fedops-logger';

export interface FedopsLoggerContextValue {
  reportAppLoaded: () => void;
  startCategorySwitchInteraction: () => void;
  stopActiveInteraction: () => void;
}

interface FedopsLoggerProviderProps {}

const fedopsLogger = createFedopsLogger('studio-templates');
const FedopsLoggerContext = createContext<FedopsLoggerContextValue>({
  reportAppLoaded: () => {},
  startCategorySwitchInteraction: () => {},
  stopActiveInteraction: () => {},
});

export const FedopsLoggerProvider: FC<
  PropsWithChildren<FedopsLoggerProviderProps>
> = ({ children }) => {
  const [activeInteraction, setActiveInteraction] = useState<string | null>();
  const reportAppLoaded = useCallback(() => {
    fedopsLogger.appLoaded();
  }, []);

  const startCategorySwitchInteraction = useCallback(() => {
    setActiveInteraction('category-switch');
    fedopsLogger.interactionStarted('category-switch');
  }, []);

  const stopActiveInteraction = useCallback(() => {
    if (activeInteraction) {
      fedopsLogger.interactionEnded(activeInteraction);
      setActiveInteraction(null);
    }
  }, [activeInteraction]);

  return (
    <FedopsLoggerContext.Provider
      value={{
        reportAppLoaded,
        startCategorySwitchInteraction,
        stopActiveInteraction,
      }}
    >
      {children}
    </FedopsLoggerContext.Provider>
  );
};

export const useFedopsLogger = () => {
  const context = React.useContext(FedopsLoggerContext);
  if (!context) {
    throw new Error(
      'useFedopsLogger must be used within a FedopsLoggerProvider',
    );
  }

  return context;
};
